<template>
  <div id="data-loader">
    <div v-if="isLoading">
      <slot name="loading">
        <p style="text-align: center">
          <em>
            The test simulation is loading...
          </em>
        </p>
      </slot>
      <loading-bar></loading-bar>
    </div>
    <slot v-else></slot>
  </div>
</template>

<script>
import LoadingBar from '@/components/singletons/loading-bar';
import WuxSimulationsMixin from '@/mixins/wux-simulations';
import ScenarioBoxMixin from '@/mixins/scenario-box';
import AccommodationsMixin from '@/mixins/accommodations';

export default {
  components: { LoadingBar },
  mixins: [WuxSimulationsMixin, ScenarioBoxMixin, AccommodationsMixin],
  props: {
    checkRoute: {
      type: Boolean,
      default: false,
    },
    doNotForward: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return { isLoading: false };
  },
  async mounted() {
    if (!this.simId) {
      return this.$router.push({ name: 'mastery#home' });
    }

    if (!this.canAccessSimulation) {
      console.error('User does not have access to this simulation');
      return this.$router.push({
        name: 'mastery#home',
      });
    }

    const hasLoaded = this.$store.getters['simulation/simsLoaded'][this.simId];

    if (!hasLoaded) {
      await this.fetchData();
      if (this.doNotForward) return;
      this.completePreviousSections();
      this.checkTimeExpired();
      this.reconcileRoute(this.correctRoute(), this.$route);
      this.completePreviousSections();
    } else if (this.checkRoute) {
      this.completePreviousSections();
      this.checkTimeExpired();
      this.reconcileRoute(this.correctRoute(), this.$route);
    }
  },
  methods: {
    checkSingleSectionTimeExpired() {
      if (this.categoryStatesFiltered.length < 1) return;
      if (this.simConfig.timeInMinutes === 0) return;

      const timeLeft = this.timeLeft(
        this.categoryStatesFiltered[0].mobileCreatedAt,
        this.simConfig.timeInMinutes,
      );

      if (timeLeft < 0) {
        this.setCategoryState(this.simulation, this.completedStatus);
      }
    },
    /**
     * checkTimeExpired - checks if the time has expired for the current category state,
     * and changes the category state to reflect that.
     */
    checkTimeExpired() {
      if (this.categoryStatesFiltered.length < 1) return;
      if (!this.isMultiSectionSimulation) return this.checkSingleSectionTimeExpired();

      const mostRecentCategoryState = [...this.categoryStatesFiltered].pop();

      const mostRecentSection = this.sections.find(section => section.id === mostRecentCategoryState.categoryId);
      const nextSection = this.sections[
          this.sections.findIndex(section => section.id === mostRecentSection.id) + 1
        ];


        // When the user has completed the current section, the break time has expired, and there is a next section
        // if all sections are completed, the user will be redirected to the performance page elsewhere
      const breakTimeInMinutes = this.getBreakTimeByCategory(mostRecentSection?.id) || mostRecentSection.breakTimeInMinutes;
      if (
        mostRecentCategoryState?.categoryStatusName === 'completed' &&
        this.timeLeft(mostRecentCategoryState.mobileCreatedAt, breakTimeInMinutes) <= 0 &&
        nextSection
      ) {
        return this.setCategoryState(nextSection, this.notStartedStatus);
      };

      // When the category state is in progress and the time has expired, the category state is set to completed
      const timeInMinutes = this.getTimeByCategory(mostRecentSection?.id) || mostRecentSection.timeInMinutes;
      if (
        mostRecentCategoryState?.categoryStatusName === 'in-progress' &&
        this.timeLeft(mostRecentCategoryState.mobileCreatedAt, timeInMinutes) <= 0
      ) {
        return this.setCategoryState(mostRecentSection, this.completedStatus);
      };

      // When the category state is not started and the time has expired for both the intro and the in-progress time,
      // the category state is set to completed
      const introTimeInMinutes = this.getIntroTimeByCategory(mostRecentSection?.id) || mostRecentSection.introTimeInMinutes;
      if (
        mostRecentCategoryState?.categoryStatusName === 'not-started' &&
        this.timeLeft(
          mostRecentCategoryState.mobileCreatedAt,
          introTimeInMinutes + timeInMinutes,
        ) <= 0
      ) {
        return this.setCategoryState(mostRecentSection, this.completedStatus);
      };

      // When the category state is not started and the time has expired for the intro time, the category state is set to in-progress
      if (
        mostRecentCategoryState?.categoryStatusName === 'not-started' &&
        this.timeLeft(mostRecentCategoryState.mobileCreatedAt, introTimeInMinutes) <= 0
      ) {
        return this.setCategoryState(mostRecentSection, this.inProgressStatus);
      };
    },
    /**
     * This method fixes situations where the user has previous category states that have not been marked as completed.
     * Only auto-completes sections that are 'in-progress', not those explicitly set to 'not-started'
     * (which may indicate an intentional admin reset).
     */
    completePreviousSections() {
      if (this.categoryStatesFiltered.length < 1) return;

      // iterate through categoryStatesFiltered skipping the last one
      for (let i = 0; i < this.categoryStatesFiltered.length - 1; i++) {
        const categoryState = this.categoryStatesFiltered[i];
        const section = this.sections.find(section => section.id === categoryState.categoryId);

        // Only auto-complete sections that are 'in-progress', not 'not-started'
        // 'not-started' may indicate an intentional reset by an admin
        if (categoryState.categoryStatusName === this.inProgressStatus.name) {
          this.setCategoryState(section, this.completedStatus);
        }
      }
    },
    singleSectionCorrectRoute(categoryState) {
      if (categoryState?.categoryStatusName === 'not-started') {
        return {
          name: 'sims#intro',
          params: {
            simId: this.simId,
            pageNum: 1,
          },
        };
      }

      if (categoryState?.categoryStatusName === 'in-progress' && this.$route?.name === 'sims#review') {
        return {
          name: 'sims#review',
          params: {
            simId: this.simId,
          },
        };
      }

      if (categoryState?.categoryStatusName === 'in-progress') {
        return {
          name: 'simFlashcard#show',
          params: {
            simId: this.simId,
            flashcardId: this.flashcardId || this.flashcards[0].id,
          },
        };
      }
    },
    correctRoute() {
      const categoryStatesList = this.categoryStatesFiltered;
      // No category states means that the simulation has not started and we should
      // go to the sims#show route
      if (categoryStatesList.length === 0) {
        return {
          name: 'sims#show',
          params: {
            simId: this.simId,
          },
        };
      };

      // If all the category states are complete, the simulation is over and we should
      // go to the sims#performance route
      const numberOfSections = this.simulation.children.length || 1;
      if (
        categoryStatesList.length === numberOfSections &&
        categoryStatesList.every(state => state.categoryStatusName === 'completed')
      ) {
        return {
          name: 'sims#performance',
          params: {
            simId: this.simId,
          },
        };
      }

      const categoryState = [...categoryStatesList].pop();
      const sectionId = categoryState.categoryId;

      if (!this.isMultiSectionSimulation) {
        return this.singleSectionCorrectRoute(categoryState);
      }

      if (categoryState?.categoryStatusName === 'not-started') {
        return {
          name: 'simSection#show',
          params: {
            simId: this.simId,
            sectionId: String(sectionId),
          },
        };
      }

      if (categoryState?.categoryStatusName === 'in-progress' && this.$route?.name === 'simSection#review') {
        return {
          name: 'simSection#review',
          params: {
            simId: this.simId,
            sectionId: String(sectionId),
          },
        };
      }

      if (categoryState?.categoryStatusName === 'in-progress') {
        return {
          name: 'simSectionFlashcard#show',
          params: {
            simId: this.simId,
            sectionId: String(sectionId),
            flashcardId: this.flashcardId || this.flashcardsBySection(sectionId)[0].id,
          },
        };
      }

      if (categoryState?.categoryStatusName === 'completed') {
        return {
          name: 'simSection#break',
          params: {
            simId: this.simId,
            sectionId: String(sectionId),
          },
        };
      }
    },
    async fetchData() {
      this.isLoading = true;
      try {
        const flashcardStateResponse = this.$store.dispatch('flashcardStates/fetchStates', ['flashcards', 'bookmarks']);
        const categoryStateResponse = await this.$store.dispatch('categoryStates/fetchCategoryStates');

        // await loading all flashcards for the simulation
        const flashcardsResponse = await this.$store.dispatch('flashcards/fetchFlashcards', {
          categoryId: this.simId,
          rateLimited: false,
        });

        // load all topics (for patient boxes) for the simulation
        const topicsResponses = [];
        if (this.isMultiSectionSimulation) {
          this.simulation?.children.forEach((catId) => {
            topicsResponses.push(this.$store.dispatch('topics/load', { ids: this.getAllTopicIds(catId) }));
          });
        } else {
          topicsResponses.push(this.$store.dispatch('topics/load', { ids: this.getAllTopicIds(this.simId) }));
          topicsResponses.push(this.$store.dispatch('topics/load', { ids: this.getAssetTrayTopics(this.simId) }));
        }

        return Promise.all([
          categoryStateResponse,
          flashcardStateResponse,
          flashcardsResponse,
          ...topicsResponses,
        ]).then(() => {
          this.$store.commit('simulation/setSimsLoaded', this.simId);
          this.isLoading = false;
        });
      } catch (error) {
        console.error(error);
        this.isLoading = false;
      }
    },
    getAllTopicIds(categoryId) {
      const flashcards = this.flashcardsBySection(categoryId);
      let topicIds = flashcards
        .map(flashcard => this.getScenarioBoxTopicId(flashcard.id))
        .filter(id => id);

      topicIds = [...new Set(topicIds)];

      return topicIds;
    },
    getAssetTrayTopics(categoryId) {
      const flashcards = this.flashcardsBySection(categoryId);
      return flashcards
        .map(flashcard => this.getAssetTrayGrouping(flashcard))
        .filter(g => g)
        .map(g => +g.assetAssociations[0].assetBContext)
        .filter(id => id);
    },
    getSection(id) {
      return this.$store.getters['categories/categories'][id];
    },
    firstIncompleteCategoryId() {
      return this.simulation?.children.find(
        catId => Object.values(this.categoryStates).find(cs => cs.categoryId === catId)?.categoryStatusName !== 'completed');
    },
    reconcileRoute(correctRoute = {}, currentRoute = {}) {
      if (
        currentRoute.name === 'simSection#review' && correctRoute.name === 'simSectionFlashcard#show'
        && currentRoute.params.simId === correctRoute.params.simId
        && currentRoute.params.sectionId === correctRoute.params.sectionId
      ) {
        return;
      }

      const reviewRoutes = [
        'simSection#review',
        'simSectionFlashcard#show',
        'sims#review',
        'simFlashcard#show',
        'simsGrouping#review',
        'simsGroupingFlashcard#review',
        'sims#outro',
      ];

      if (
        correctRoute.name === 'sims#performance'
        && reviewRoutes.includes(currentRoute.name)
        && currentRoute.params.simId === correctRoute.params.simId
      ) {
        return;
      }

      if (correctRoute.name !== currentRoute.name) {
        this.$router.push(correctRoute);
      } else if (correctRoute.params.simId !== currentRoute.params.simId) {
        this.$router.push(correctRoute);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
#data-loader {
  padding: 0;
}
</style>
